<script setup>
import ChatInput from '@/components/chat/ChatInput.vue'
import ChatMessage from '@/components/chat/ChatMessage.vue'
import ChatMessages from '@/components/chat/ChatMessages.vue'
import {Channel} from '@/models/channel.model.js'
import {useMessaging} from '@/composables/use-messaging.js'
import {useDebugMode} from '@/composables/use-debug-mode.js'
import {watchArray, useThrottleFn} from '@vueuse/core'
import {toRefs, ref, computed} from 'vue'

//Emits and props
const emit = defineEmits([
  'message',
  'message-seen',
])
const props = defineProps({
  messages: {
    type: Array,
    default: () => [],
  },
  scrollToMessageId: {
    type: String,
    default: null,
  },
  lastSeenMessageId: {
    type: String,
    default: null,
  },
  channel: {
    type: Channel,
    default: null,
  },
  isChatDisabled: {
    type: Boolean,
    default: false,
  },
  showCourseNames: {
    type: Boolean,
    default: false,
  },
  showTimestamps: {
    type: Boolean,
    default: false,
  },
  showNew: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: '',
  },
})

//Create message
const {messages, lastSeenMessageId, scrollToMessageId} = toRefs(props)
const lastMessage = computed(() => messages.value[messages.value.length - 1])
const {appendIncomingMessage} = useMessaging(messages)
const {debugLog} = useDebugMode()

//Helper to group messages by date
const groupMessagesByDate = () => {
  let previousMessage = null
  for (const message of messages.value) {
    message.setHelperFlags(previousMessage)
    previousMessage = message
  }
}

//This ref tracks the ID of the message that should scroll into view. Initially,
//we set this to the last message ID, so that initially it scrolls to the last
//message seen on the first load. Later, we can scroll to the first new message
//that appears from the server, or the last message sent by the user.
const scrollIntoViewMessageId = ref(lastSeenMessageId.value)
const lastHandledScrollToMessageId = ref(null)
const hasSeenLastMessage = ref(false)
const hasSetSeenLastMessageFlag = ref(false)

//Send message
const onInput = text => {
  const message = appendIncomingMessage(text)
  emit('message', {message})
  debugLog('User has sent a message')
  hasSeenLastMessage.value = true
}

//Handle message seen
const handleMessageSeen = message => {
  debugLog('User has seen message:', message.id)
  emit('message-seen', {message})

  //Set flag when user sees the last message
  if (messages.value[messages.value.length - 1].id === message.id) {
    debugLog('User has seen the last message')
    hasSeenLastMessage.value = true
  }
}

//Scroll to new message
const scrollToNewMessage = (isInitialLoad = false) => {

  //Only do this when there is already a last seen message ID
  if (!lastSeenMessageId.value) {
    debugLog('No last seen message ID present: not scrolling to new message')
    return
  }

  //Check if there are any new messages that we haven't seen yet
  //We do this by comparing the index of the last seen message with the index of the last message in the array
  const outgoing = messages.value.filter(message => message.isOutgoing)
  const lastSeenIndex = outgoing.findIndex(message => message.id === lastSeenMessageId.value)
  const hasNewMessages = lastSeenIndex < outgoing.length - 1
  const firstNewMessage = outgoing[lastSeenIndex + 1]

  //On initial load, if there are new messages, scroll to the first new message
  //Otherwise, only scroll if the user has previously seen the last message
  const shouldScrollToNewMessage = (isInitialLoad || hasSeenLastMessage.value) && hasNewMessages && firstNewMessage

  //Log
  debugLog('Is initial load:', isInitialLoad)
  debugLog('Messages:', messages.value)
  debugLog('Last seen message ID:', lastSeenMessageId.value)
  debugLog('Last seen index:', lastSeenIndex)
  debugLog('Has seen last message:', hasSeenLastMessage.value)
  debugLog('Has new messages:', hasNewMessages)
  debugLog('First new message:', firstNewMessage?.id)

  //Scroll to new message
  if (shouldScrollToNewMessage) {
    debugLog('Should scroll to first new message:', firstNewMessage.id)
    scrollIntoViewMessageId.value = firstNewMessage.id
  }

  //Reset flag if there are new messages
  if (hasNewMessages) {
    debugLog('Resetting seen last message flag because there are new messages')
    hasSeenLastMessage.value = false
  }
}

//Should scroll into view check
const shouldScrollIntoView = message => {
  if (message.id === scrollIntoViewMessageId.value) {
    debugLog('Message matches scroll into view message ID:', message.id)
    return true
  }
  if (message.isNew && message.isIncoming) {
    debugLog('Message is new and incoming:', message.id)
    return true
  }
  return false
}

//Handler for message array changes
const handleMessageArrayChange = () => {

  //Check if this is the initial load
  const isInitialLoad = !hasSetSeenLastMessageFlag.value

  //Set initial flag value so that we correctly scroll to the right message
  if (isInitialLoad) {
    hasSetSeenLastMessageFlag.value = true
    hasSeenLastMessage.value = lastSeenMessageId.value === lastMessage.value?.id
    debugLog('Set initial value for last message seen flag to:', hasSeenLastMessage.value)
  }

  //Group messages by date and scroll to new message
  groupMessagesByDate()

  //If a specific message id is requested (e.g. via query param), prefer that one
  if (scrollToMessageId.value && scrollToMessageId.value !== lastHandledScrollToMessageId.value) {
    const hasTargetMessage = messages.value.some(message => message.id === scrollToMessageId.value)
    if (hasTargetMessage) {
      debugLog('Scrolling to requested message id:', scrollToMessageId.value)
      scrollIntoViewMessageId.value = scrollToMessageId.value
      lastHandledScrollToMessageId.value = scrollToMessageId.value
      return
    }
  }

  scrollToNewMessage(isInitialLoad)
}

//Watch messages array for future changes (throttled)
const handleMessageArrayChangeThrottled = useThrottleFn(handleMessageArrayChange, 250)
watchArray(messages, handleMessageArrayChangeThrottled)
</script>

<template>
  <div :class="$style.container">
    <ChatMessages :show-jump-to-bottom="messages.length > 1">
      <template #default="{scrollToBottom}">
        <ChatMessage
          v-for="(message, index) in messages"
          :key="index"
          :message="message"
          :has-error="!!message.error"
          :channel="channel"
          :show-new="showNew"
          :show-timestamps="showTimestamps"
          :show-course-names="showCourseNames"
          :should-scroll-into-view="shouldScrollIntoView(message)"
          @seen="handleMessageSeen(message)"
          @scroll-to-bottom="scrollToBottom()"
        />
      </template>
    </ChatMessages>
    <ChatInput
      v-bind="$attrs"
      :is-disabled="isChatDisabled"
      :placeholder="placeholder"
      @input="onInput($event.text)"
    />
  </div>
</template>

<style module lang="scss">
.container {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  overflow: auto;
  max-width: 100%;
  padding: 1px; /* For chat input focus ring not to get cut off */
}
</style>
